import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  AccessibilityPreferences,
  SubmissionsUIPreferences,
  TableUIPreferences,
  TimesheetUIPreferences,
  UIPreference,
  UserManagementPreferences,
} from '../models/ui-preferences';
import { User } from '../models/user';
import { UsersService } from './user.service';

@Injectable({ providedIn: 'root' })
export class UiPreferencesService {
  constructor(private usersService: UsersService) {}

  fromUser(user: User | undefined | null): UIPreference {
    return this.fromJson(user?.userUIPreference?.data);
  }

  fromJson(data: string | undefined | null): UIPreference {
    if (!data) return new UIPreference();

    try {
      return { ...new UIPreference(), ...JSON.parse(data) };
    } catch (error) {
      console.error('Unable to parse UI preferences.', error);
      return new UIPreference();
    }
  }

  save(userId: number, preference: UIPreference): Observable<UIPreference> {
    return this.usersService.savePreference(preference, userId);
  }

  patchTimesheet(
    preference: UIPreference,
    patch: Partial<TimesheetUIPreferences>,
  ): UIPreference {
    preference.timesheet = this.patchSection(
      new TimesheetUIPreferences(),
      preference.timesheet,
      patch,
    );
    return preference;
  }

  patchEfforts(
    preference: UIPreference,
    patch: Partial<TableUIPreferences>,
  ): UIPreference {
    preference.efforts = this.patchSection(
      new TableUIPreferences(),
      preference.efforts,
      patch,
    );
    return preference;
  }

  patchProjects(
    preference: UIPreference,
    patch: Partial<TableUIPreferences>,
  ): UIPreference {
    preference.projects = this.patchSection(
      new TableUIPreferences(),
      preference.projects,
      patch,
    );
    return preference;
  }

  patchUsers(
    preference: UIPreference,
    patch: Partial<UserManagementPreferences>,
  ): UIPreference {
    preference.users = this.patchSection(
      new UserManagementPreferences(),
      preference.users,
      patch,
    );
    return preference;
  }

  patchSubmissions(
    preference: UIPreference,
    patch: Partial<SubmissionsUIPreferences>,
  ): UIPreference {
    preference.submissions = this.patchSection(
      new SubmissionsUIPreferences(),
      preference.submissions,
      patch,
    );
    return preference;
  }

  patchAccessibility(
    preference: UIPreference,
    patch: Partial<AccessibilityPreferences>,
  ): UIPreference {
    preference.accessibility = this.patchSection(
      new AccessibilityPreferences(),
      preference.accessibility,
      patch,
    );
    return preference;
  }

  private patchSection<T extends object>(
    defaults: T,
    current: T | undefined,
    patch: Partial<T>,
  ): T {
    return Object.assign(defaults, current, this.withDefinedValues(patch));
  }

  private withDefinedValues<T extends object>(patch: Partial<T>): Partial<T> {
    return Object.fromEntries(
      Object.entries(patch).filter(([, value]) => value !== undefined),
    ) as Partial<T>;
  }
}
